import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import * as THREE from 'three';
import { ExportDialogComponent } from './export-dialog/export-dialog.component';
import { ScreenshotService2 } from '../services/screenshot.service';
import { ReportCreator } from '../services/report-creator';
import { Packer } from 'docx';

export interface ExportManagerDependencies {
    scene: THREE.Scene;
    camera: THREE.Camera;
    renderer: THREE.WebGLRenderer;
    project: any;
    customerid: string;
    projectid: string;
    afs: AngularFirestore;
    dialog: MatDialog;
    toastr: ToastrService;
    screenshotService2: ScreenshotService2;
    updateThreeFrame: () => void;
    updateLastSaveTime: () => void;
}

@Injectable({
    providedIn: 'root'
})
export class ExportManager {
    private dependencies: ExportManagerDependencies | null = null;

    setDependencies(deps: ExportManagerDependencies) {
        this.dependencies = deps;
    }

    private ensureDependencies() {
        if (!this.dependencies) {
            throw new Error('ExportManager dependencies not set. Call setDependencies() first.');
        }
        return this.dependencies;
    }

    /**
     * Export scene to various formats via dialog
     */
    public exportScene(): void {
        const deps = this.ensureDependencies();

        const dialogRef = deps.dialog.open(ExportDialogComponent, {
            data: { scene: deps.scene }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                deps.toastr.success('Scene exported successfully');
            }
        });
    }

    /**
     * Export to STEP format
     */
    public exportToSTEP(): void {
        const deps = this.ensureDependencies();

        const dialogRef = deps.dialog.open(ExportDialogComponent, {
            data: { scene: deps.scene }
        });
    }

    /**
     * Take a screenshot of the current scene
     */
    public async takeScreenshot(overwrite: boolean = false): Promise<void> {
        const deps = this.ensureDependencies();

        try {
            if (!deps.renderer) {
                console.error("Renderer not available for screenshot.");
                return;
            }

            await deps.screenshotService2.takeScreenshot(
                deps.scene,
                deps.camera,
                deps.renderer,
                deps.projectid,
                deps.customerid,
                deps.project,
                deps.afs,
                overwrite
            );
        } catch (error) {
            console.error("Error taking screenshot:", error);
            deps.toastr.error('Error taking screenshot: ' + error.message);
        }
    }

    /**
     * Generate a Word document report
     */
    public async generateReport(): Promise<void> {
        const deps = this.ensureDependencies();

        try {
            const doc = await new ReportCreator(deps.scene, deps.camera, deps.renderer).generateReport();
            const blob = await Packer.toBlob(doc);

            // Download the report
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${deps.project.name || 'model'}_report.docx`;
            link.click();
            window.URL.revokeObjectURL(url);

            deps.updateThreeFrame();
            deps.toastr.success('Report generated successfully');
        } catch (error) {
            console.error('Error generating report:', error);
            deps.toastr.error('Error generating report: ' + error.message);
        }
    }

    /**
     * Save project with camera state and menu settings
     */
    public async saveProject(excels: any, hot: any, PROJECT: string): Promise<void> {
        const deps = this.ensureDependencies();

        try {
            // Get the gui panel element and its current state
            const guiPanel = document.getElementById('gui-panel');
            if (guiPanel) {
                const width = guiPanel.offsetWidth;
                const height = guiPanel.offsetHeight;
                const computedStyle = window.getComputedStyle(guiPanel);

                // Update menu settings with current dimensions and position
                deps.project.menuSettings.width = width;
                deps.project.menuSettings.height = height;

                // Get current position from computed styles
                if (computedStyle.left && computedStyle.left !== 'auto') {
                    deps.project.menuSettings.menuleft = computedStyle.left;
                }
                if (computedStyle.top && computedStyle.top !== 'auto') {
                    deps.project.menuSettings.menutop = computedStyle.top;
                }
                if (computedStyle.right && computedStyle.right !== 'auto' && deps.project.menuSettings.menufloat === 'right') {
                    deps.project.menuSettings.menuleft = computedStyle.right;
                }
            }

            // Save camera state
            const cameraState = {
                matrix: deps.camera.matrix.toArray(),
                position: deps.camera.position.toArray(),
                quaternion: deps.camera.quaternion.toArray(),
                zoom: (deps.camera as any).zoom,
                target: (deps.camera as any).controls?.target?.toArray() || [0, 0, 0]
            };

            // Save camera state as cameraview in project
            deps.project.cameraview = JSON.stringify(cameraState);

            // Update last save time
            deps.project.lastsave = new Date().getTime();

            // Create config model (this would need to be passed in or extracted)
            // var configModel = await this.createConfigModel();
            // deps.project.configmodel = {
            //   ...deps.project.configmodel,
            //   ...configModel
            // };

            // Save to Firestore
            await deps.afs.doc(`projects/userprojects/${deps.customerid}/${deps.projectid}`).update(
                JSON.parse(JSON.stringify(deps.project))
            );

            console.log('Project saved successfully', deps.project);

            // Save cells
            await this.saveCells(excels, hot, PROJECT);

            if (hot) {
                hot.render();
            }

            deps.updateLastSaveTime();
        } catch (error) {
            console.error('Error saving project:', error);
            deps.toastr.error("Error saving: " + error.message, "", {
                timeOut: 4000
            });
            throw error;
        }
    }

    /**
     * Save cells data
     */
    public async saveCells(excels: any, hot: any, PROJECT: string): Promise<void> {
        const deps = this.ensureDependencies();

        try {
            const cellformulas = await excels[PROJECT].getSheetSerializedByIndex(0); // Assuming sheet 0
            const result = await excels[PROJECT].saveCells();
            console.log('saveCells', result);
            return result;
        } catch (error) {
            console.error('saveCells error', error);
            deps.toastr.error('Error saving cells: ' + error.message);
            throw error;
        }
    }

    /**
     * Export current model as shareable URL
     */
    public getCurrentModelShareUrl(customerid: string, projectid: string): string {
        if (projectid) {
            const baseUrl = window.location.origin;
            return `${baseUrl}/view/${customerid}/${projectid}`;
        }
        return window.location.href;
    }

    /**
     * Dispose of resources
     */
    public dispose(): void {
        this.dependencies = null;
    }
} 